import { useState } from "react";
import { Box, Chip, CircularProgress, Grid2 as Grid, Switch, Typography } from "@mui/material";
import { useNotify, useRefresh } from "react-admin";
import { ResourceName, AlertType, type TaskTemplate } from "@/types";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";

interface NotificationSettingsSectionProps {
  template: TaskTemplate;
}

/**
 * Labeled toggle row with description text.
 */
const ToggleRow = ({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <Grid container spacing={1} sx={{ py: 1, alignItems: "flex-start" }}>
    <Grid size={{ xs: 8 }}>
      <Typography variant="body2" fontWeight={500}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.2, display: "block" }}>
        {description}
      </Typography>
    </Grid>
    <Grid size={{ xs: 4 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
      <Switch checked={checked} disabled={disabled} size="small" onChange={(_, val) => onChange(val)} />
    </Grid>
  </Grid>
);

export const NotificationSettingsSection = ({ template }: NotificationSettingsSectionProps) => {
  const { canEdit } = useResourcePermissions(ResourceName.TASK_TEMPLATES);
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();
  const [isUpdating, setIsUpdating] = useState(false);

  const notification = template.alert_notification;

  if (!notification) {
    return (
      <Typography variant="body2" color="text.secondary">
        No notification settings configured.
      </Typography>
    );
  }

  const handleToggle = async (field: "notify_at_creation" | "notify_record_owner", checked: boolean) => {
    setIsUpdating(true);
    const apiUrl = getPartnerAwareApiUrl();

    try {
      await httpClient(`${apiUrl}/${ResourceName.TASK_TEMPLATES}/${template.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          alert_notification: {
            ...notification,
            [field]: checked,
          },
        }),
      });
      notify("Notification settings updated", { type: AlertType.SUCCESS });
      refresh();
    } catch (error) {
      notifyError(error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box>
      {isUpdating && (
        <Box display="flex" justifyContent="center" py={0.5}>
          <CircularProgress size={18} />
        </Box>
      )}

      <ToggleRow
        label="Notify at Creation"
        description="Alert when a task is auto-created from this template."
        checked={!!notification.notify_at_creation}
        disabled={!canEdit || isUpdating}
        onChange={(checked) => handleToggle("notify_at_creation", checked)}
      />

      <ToggleRow
        label="Notify Record Owner"
        description="Also notify the owner of the related record."
        checked={!!notification.notify_record_owner}
        disabled={!canEdit || isUpdating}
        onChange={(checked) => handleToggle("notify_record_owner", checked)}
      />

      {/* Custom Recipients */}
      <Box mt={2}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
          Custom Recipients
        </Typography>
        {notification.custom_recipients && notification.custom_recipients.length > 0 ? (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {notification.custom_recipients.map((recipient) => (
              <Chip key={recipient} label={recipient} size="small" />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.disabled" fontStyle="italic">
            No custom recipients configured
          </Typography>
        )}
      </Box>
    </Box>
  );
};
